import { todayKey } from '../../../../utils/datetime';
import React from 'react';
import Modal from '../../../../components/Modal';
import { Button } from '../../../../components';
import { InitialsAvatar, hhmm } from '../shared';
import { useDutyRoster } from '../DutyRosterContext';
import { LEAVE_TYPES, unavailabilityLabel } from '../../../../utils/leaveTypes';

const EmployeeDetailsModal = () => {
    const { employeeModal, employeeUnavailability, unavailabilityLoading, unavailForm, setUnavailForm, unavailSaving, closeEmployeeModal, submitUnavailability, removeUnavailability, isEmployeeUnavailableOn } = useDutyRoster();
    return (
            <Modal
                isOpen={!!employeeModal}
                onClose={closeEmployeeModal}
                title={employeeModal ? `${employeeModal.emp.name} · Details` : ''}
                maxWidth="max-w-[560px]"
            >
                {employeeModal && (() => {
                    const { emp, date } = employeeModal;
                    const todayStr = todayKey();
                    const unavailFor = isEmployeeUnavailableOn(emp, date);
                    const roster = emp.rosterDetails;
                    let workingDays = roster?.working_days;
                    if (typeof workingDays === 'string') {
                        try { workingDays = JSON.parse(workingDays); } catch (e) { workingDays = []; }
                    }
                    if (!Array.isArray(workingDays)) workingDays = [];

                    return (
                        <div className="space-y-5 pt-1">
                            {/* Header card */}
                            <div className="flex items-center gap-4 bg-gray-50 dark:bg-[#20242c] p-4 rounded-2xl border border-gray-100 dark:border-gray-800">
                                <InitialsAvatar name={emp.name} size="lg" />
                                <div className="flex-1 min-w-0">
                                    <h3 className="text-[16px] font-bold text-gray-900 dark:text-gray-100 truncate">{emp.name}</h3>
                                    <p className="text-gray-500 dark:text-gray-400 text-[12px]">{emp.email || '—'}</p>
                                    {roster && (
                                        <p className="text-[11px] text-[#64748B] dark:text-gray-400 mt-1 font-medium">
                                            Roster: {hhmm(roster.shift_start)}–{hhmm(roster.shift_end)} · {workingDays.length} day{workingDays.length === 1 ? '' : 's'}/week
                                        </p>
                                    )}
                                </div>
                                {unavailFor ? (
                                    <span className="text-[10px] font-black uppercase tracking-wider px-2.5 py-1 rounded-full bg-rose-100 dark:bg-red-900/10 text-rose-700 dark:text-red-400">
                                        Unavailable · {unavailabilityLabel(unavailFor)}
                                    </span>
                                ) : (
                                    <span className="text-[10px] font-black uppercase tracking-wider px-2.5 py-1 rounded-full bg-emerald-100 dark:bg-emerald-900/10 text-emerald-700 dark:text-emerald-400">
                                        Available
                                    </span>
                                )}
                            </div>

                            {/* Add holiday form */}
                            <div className="space-y-3 p-4 rounded-2xl border border-[#E2E8F0] dark:border-gray-800">
                                <h4 className="text-[12px] font-black uppercase tracking-widest text-[#475569] dark:text-gray-400">Add unavailable window</h4>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <div className="flex items-center justify-between mb-1">
                                            <label className="text-[11px] font-bold text-[#64748B] dark:text-gray-400">From</label>
                                            <button
                                                type="button"
                                                onClick={() => setUnavailForm((f) => ({ ...f, start_date: todayStr, end_date: f.end_date && f.end_date >= todayStr ? f.end_date : todayStr }))}
                                                className="text-[10px] font-bold text-[#2563EB] hover:underline"
                                            >
                                                Today
                                            </button>
                                        </div>
                                        <input
                                            type="date"
                                            min={todayStr.substring(0, 4) + '-01-01'}
                                            value={unavailForm.start_date}
                                            onChange={(e) => setUnavailForm((f) => ({
                                                ...f,
                                                start_date: e.target.value,
                                                // Auto-fill end if empty so single-day holidays are quick.
                                                end_date: f.end_date && f.end_date >= e.target.value ? f.end_date : e.target.value,
                                            }))}
                                            className="w-full px-3 py-2 rounded-lg border border-[#E2E8F0] dark:border-gray-700 dark:bg-[#31373f] dark:text-gray-200 focus:border-blue-500 outline-none text-[13px]"
                                        />
                                    </div>
                                    <div>
                                        <div className="flex items-center justify-between mb-1">
                                            <label className="text-[11px] font-bold text-[#64748B] dark:text-gray-400">To</label>
                                            <button
                                                type="button"
                                                onClick={() => setUnavailForm((f) => ({ ...f, end_date: todayStr, start_date: f.start_date && f.start_date <= todayStr ? f.start_date : todayStr }))}
                                                className="text-[10px] font-bold text-[#2563EB] hover:underline"
                                            >
                                                Today
                                            </button>
                                        </div>
                                        <input
                                            type="date"
                                            value={unavailForm.end_date}
                                            min={unavailForm.start_date || undefined}
                                            onChange={(e) => setUnavailForm((f) => ({ ...f, end_date: e.target.value }))}
                                            className="w-full px-3 py-2 rounded-lg border border-[#E2E8F0] dark:border-gray-700 dark:bg-[#31373f] dark:text-gray-200 focus:border-blue-500 outline-none text-[13px]"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-[11px] font-bold text-[#64748B] dark:text-gray-400 mb-1">Reason</label>
                                    <div className="flex flex-wrap gap-1.5">
                                        {LEAVE_TYPES.map((t) => (
                                            <button
                                                key={t.key}
                                                title={t.help}
                                                onClick={() => setUnavailForm((f) => ({ ...f, leave_type: t.key }))}
                                                className={`px-3 py-1.5 rounded-full text-[11px] font-bold border transition ${unavailForm.leave_type === t.key
                                                        ? 'bg-blue-600 text-white border-blue-600'
                                                        : 'bg-white dark:bg-[#31373f] text-[#475569] dark:text-gray-400 border-[#E2E8F0] dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-[#31373f]'
                                                    }`}
                                            >
                                                {t.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                <textarea
                                    rows={2}
                                    placeholder="Notes (optional)"
                                    value={unavailForm.notes}
                                    onChange={(e) => setUnavailForm((f) => ({ ...f, notes: e.target.value }))}
                                    className="w-full px-3 py-2 rounded-lg border border-[#E2E8F0] dark:border-gray-700 dark:bg-[#31373f] dark:text-gray-200 focus:border-blue-500 outline-none text-[12px] resize-none"
                                />
                                <Button
                                    variant="primary"
                                    block
                                    onClick={submitUnavailability}
                                    loading={unavailSaving}
                                >
                                    Save
                                </Button>
                            </div>

                            {/* Existing entries */}
                            <div>
                                <h4 className="text-[12px] font-black uppercase tracking-widest text-[#475569] dark:text-gray-400 mb-2">Upcoming & past unavailability</h4>
                                {unavailabilityLoading ? (
                                    <p className="text-[12px] text-[#94A3B8] dark:text-gray-500">Loading…</p>
                                ) : employeeUnavailability.length === 0 ? (
                                    <p className="text-[12px] text-[#94A3B8] dark:text-gray-500 italic">No unavailability recorded yet.</p>
                                ) : (
                                    <ul className="space-y-2 max-h-[220px] overflow-y-auto pr-1">
                                        {employeeUnavailability.map((u) => {
                                            const isCurrent = todayStr >= u.start_date && todayStr <= u.end_date;
                                            const isPast = u.end_date < todayStr;
                                            return (
                                                <li
                                                    key={u.id}
                                                    className={`flex items-center justify-between gap-2 px-3 py-2 rounded-lg border ${isCurrent
                                                            ? 'bg-rose-50 dark:bg-red-900/10 border-rose-200 dark:border-red-900/50'
                                                            : isPast
                                                                ? 'bg-gray-50 dark:bg-[#20242c] border-[#F1F5F9] dark:border-gray-800 opacity-60'
                                                                : 'bg-amber-50/40 dark:bg-amber-900/10 border-amber-100 dark:border-amber-900/50'
                                                        }`}
                                                >
                                                    <div className="min-w-0 flex-1">
                                                        <p className="text-[12px] font-bold text-[#1E293B] dark:text-gray-200 truncate">
                                                            {u.start_date}{u.end_date !== u.start_date ? ` → ${u.end_date}` : ''}
                                                            <span className="ml-2 text-[10px] font-medium text-[#64748B] dark:text-gray-400">{unavailabilityLabel(u) || '—'}</span>
                                                        </p>
                                                        {u.notes && (
                                                            <p className="text-[10px] text-[#64748B] dark:text-gray-400 italic mt-0.5 truncate">{u.notes}</p>
                                                        )}
                                                    </div>
                                                    <Button
                                                        variant="danger-soft"
                                                        size="sm"
                                                        onClick={() => removeUnavailability(u.id)}
                                                        title="Remove"
                                                    >
                                                        Remove
                                                    </Button>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                )}
                            </div>

                            <Button
                                variant="secondary"
                                block
                                onClick={closeEmployeeModal}
                            >
                                Close
                            </Button>
                        </div>
                    );
                })()}
            </Modal>
    );
};

export default EmployeeDetailsModal;
